import { Box, Typography, Paper, Button, Divider, Chip, IconButton, Tooltip } from '@mui/material';
import {
  ArrowBack,
  Edit,
  Person,
  Business,
  Email,
  Phone,
  LocationOn,
  Visibility
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import AppLayout from '../../components/layout/AppLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusChip from '../../components/common/StatusChip';
import clientiService from '../../services/clientiService';
import scadenzeService from '../../services/scadenzeService';
import type { Scadenza } from '../../types/scadenza.types';

const ClienteDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: cliente, isLoading } = useQuery({
    queryKey: ['cliente', id],
    queryFn: () => clientiService.getById(id!)
  });

  const { data: clienteStats } = useQuery({
    queryKey: ['cliente-stats', id],
    queryFn: () => clientiService.getStats(id!),
    enabled: !!cliente
  });

  const { data: scadenzeData } = useQuery({
    queryKey: ['scadenze-cliente', id],
    queryFn: () => scadenzeService.getAll({ page: 1, limit: 50, clienteId: id }),
    enabled: !!cliente
  });

  if (isLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento cliente..." />
      </AppLayout>
    );
  }

  if (!cliente) {
    return (
      <AppLayout>
        <Typography>Cliente non trovato</Typography>
      </AppLayout>
    );
  }

  const nomeCompleto = cliente.personaGiuridica
    ? cliente.nome
    : `${cliente.cognome} ${cliente.nome}`.trim();

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/clienti')} variant="outlined">
              Indietro
            </Button>
            <Typography variant="h4" component="h1" fontWeight={600}>
              Dettaglio Cliente
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/clienti/${id}/edit`)}
          >
            Modifica
          </Button>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
          {/* Informazioni Anagrafiche */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {cliente.personaGiuridica ? (
                <Business color="primary" />
              ) : (
                <Person color="primary" />
              )}
              <Typography variant="h6" fontWeight={600}>
                Informazioni Anagrafiche
              </Typography>
              <Chip
                label={cliente.personaGiuridica ? 'P. Giuridica' : 'P. Fisica'}
                color={cliente.personaGiuridica ? 'primary' : 'default'}
                size="small"
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {cliente.personaGiuridica ? 'Ragione Sociale' : 'Cognome e Nome'}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {nomeCompleto}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {cliente.personaGiuridica ? 'P.IVA' : 'Codice Fiscale'}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {cliente.personaGiuridica ? (cliente.partitaIva || '-') : (cliente.codiceFiscale || '-')}
                </Typography>
              </Box>
              {!cliente.personaGiuridica && cliente.partitaIva && (
                <Box>
                  <Typography variant="body2" color="text.secondary">P.IVA</Typography>
                  <Typography variant="body1" fontWeight={600}>{cliente.partitaIva}</Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Contatti */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Email color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Contatti
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {cliente.email || '-'}
                </Typography>
              </Box>
              {cliente.pec && (
                <Box>
                  <Typography variant="body2" color="text.secondary">PEC</Typography>
                  <Typography variant="body1" fontWeight={600}>{cliente.pec}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">Telefono</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {cliente.telefono || '-'}
                </Typography>
              </Box>
              {(cliente.indirizzo || cliente.citta) && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Indirizzo</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={600}>
                    {[cliente.indirizzo, cliente.cap, cliente.citta, cliente.provincia ? `(${cliente.provincia})` : '']
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Statistiche Scadenze */}
          {clienteStats && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Riepilogo Scadenze
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h4" fontWeight={600} color="warning.main">
                    {clienteStats.scadenze?.attiva || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Attive</Typography>
                </Box>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h4" fontWeight={600} color="error.main">
                    {clienteStats.scadenze?.in_scadenza || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">In Scadenza</Typography>
                </Box>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h4" fontWeight={600} color="success.main">
                    {clienteStats.scadenze?.completata || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Completate</Typography>
                </Box>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h4" fontWeight={600} color="text.secondary">
                    {clienteStats.scadenze?.annullata || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Annullate</Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Note */}
          {cliente.note && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Note
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1">{cliente.note}</Typography>
            </Paper>
          )}
        </Box>

        {/* Lista Scadenze del Cliente */}
        {scadenzeData && scadenzeData.data && scadenzeData.data.length > 0 && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Scadenze del Cliente ({scadenzeData.total})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {scadenzeData.data.map((scadenza: Scadenza) => (
                <Paper
                  key={scadenza.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' },
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => navigate(`/scadenze/${scadenza.id}`)}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {scadenza.tipologia?.nome}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scadenza: {format(new Date(scadenza.dataScadenza), 'dd MMMM yyyy', { locale: it })}
                    </Typography>
                    {scadenza.descrizione && (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
                        {scadenza.descrizione}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip stato={scadenza.stato} size="small" />
                    <Tooltip title="Visualizza">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/scadenze/${scadenza.id}`);
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        )}

        {/* Info creazione */}
        <Box sx={{ mt: 3, display: 'flex', gap: 3, color: 'text.secondary' }}>
          <Typography variant="caption">
            Creato il: {format(new Date(cliente.createdAt), 'dd/MM/yyyy HH:mm', { locale: it })}
          </Typography>
          <Typography variant="caption">
            Ultimo aggiornamento: {format(new Date(cliente.updatedAt), 'dd/MM/yyyy HH:mm', { locale: it })}
          </Typography>
        </Box>
      </Box>
    </AppLayout>
  );
};

export default ClienteDetailPage;
